/**
 * Tool Result Summary Generator
 *
 * Generates human-readable, output-derived summaries from completed tool calls.
 * The single-call `getToolResultLine` is used by the compact per-tool status line
 * (`ToolThinkingDisplay`) so that normal (non-debug) mode surfaces the essential
 * result value — not just an input-derived phrase.
 */

type ToolOutput = Record<string, unknown>;
type ToolInput = Record<string, unknown>;

/**
 * Cube returns rows keyed by member (e.g. "Candidate.count"). Turn a member key
 * into a short human label, preferring the query annotation's shortTitle/title
 * and falling back to the member's trailing segment.
 */
function humaniseMember(member: string, annotation?: Record<string, unknown>): string {
  const measures = (annotation?.measures as Record<string, { shortTitle?: string; title?: string }>) || {};
  const dimensions = (annotation?.dimensions as Record<string, { shortTitle?: string; title?: string }>) || {};
  const meta = measures[member] || dimensions[member];
  const label = meta?.shortTitle || meta?.title;
  if (label) return label.toLowerCase();
  // "Candidate.count" → "count"
  const segment = member.includes(".") ? member.split(".").pop()! : member;
  return segment.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

/** Format a single Cube result row as compact "label **value**" pairs (capped). */
function formatQueryRow(
  row: Record<string, unknown>,
  annotation?: Record<string, unknown>
): string {
  const entries = Object.entries(row).slice(0, 4);
  return entries
    .map(([member, value]) => `${humaniseMember(member, annotation)} **${String(value)}**`)
    .join(", ");
}

interface ToolCallInfo {
  toolName: string;
  // Widened to `unknown` so an AggregatedToolCall (whose input/output are
  // `unknown` until parsed) can be passed directly; narrowed via casts below.
  input?: unknown;
  finalOutput?: unknown;
}

/**
 * Summary generators for action tools that produce meaningful results.
 * Each returns a sentence summarising what happened, using data from the result.
 */
const summaryGenerators: Record<string, (input: ToolInput, output: ToolOutput) => string> = {
  findRecords: (_input, output) => {
    const results = (output.results as Array<{ name: string; status: string; options?: unknown[] }> | undefined) ?? [];
    return results
      .map((r) =>
        r.status === "unique" ? `Found **${r.name}**` : r.status === "ambiguous" ? `${r.options?.length ?? "Several"} matches for **${r.name}**` : `No match for **${r.name}**`
      )
      .join(" · ");
  },

  createFloat: (_input, output) => {
    const float = output.float as Record<string, unknown> | undefined;
    const name = float?.name || float?.heading || (_input.title as string) || "Untitled";
    const candidateCount = output.candidate_count || (float?.candidates as unknown[] | undefined)?.length || 0;
    const qualityScore = float?.quality_score;
    const shareLink = float?.share_link;
    const urls = output.urls as Record<string, string> | undefined;
    const editUrl = urls?.edit;

    let summary = `Created float **"${name}"** with ${candidateCount} candidate${candidateCount !== 1 ? "s" : ""}`;
    if (qualityScore) summary += ` (quality score: ${qualityScore})`;
    summary += `. Status: ${float?.status || "draft"}.`;
    if (shareLink) summary += ` [Share link](${shareLink})`;
    if (editUrl) summary += ` · [Edit](${editUrl})`;
    return summary;
  },

  createCandidate: (_input, output) => {
    // A partial or failed create, or a CV import: the tool's own text says what happened.
    if (output.success !== true || output.via === "cv_import") {
      const detail = output.error ?? output.message;
      if (typeof detail === "string") return detail;
    }
    const candidate = output.candidate as Record<string, unknown> | undefined;
    const name = candidate?.name || (_input.name as string) || "Unknown";
    const title = candidate?.position_title || candidate?.title || "";
    let summary = `Created candidate **${name}**`;
    if (title) summary += ` — ${title}`;
    summary += ".";
    return summary;
  },

  importCandidateFromResume: (_input, output) => {
    const detail = output.message ?? output.error;
    return typeof detail === "string" ? detail : "CV import started.";
  },

  createContact: (_input, output) => {
    const contact = output.contact as Record<string, unknown> | undefined;
    const name = contact?.full_name || contact?.name ||
      `${(_input.firstName as string) || ""} ${(_input.lastName as string) || ""}`.trim() || "Unknown";
    const company = contact?.company || (_input.company as string) || "";
    let summary = `Created contact **${name}**`;
    if (company) summary += ` at ${company}`;
    summary += ".";
    return summary;
  },

  createFloatEmail: (_input, output) => {
    const email = output.email as Record<string, unknown> | undefined;
    const subject = email?.subject || (_input.subject as string) || "";
    let summary = "Email draft created";
    if (subject) summary += `: "${subject}"`;
    summary += ".";
    return summary;
  },

  sendTestEmail: (_input, output) => {
    const message = output.message as string | undefined;
    return message || "Test email sent successfully.";
  },

  sendEmailCampaign: (_input, output) => {
    const message = output.message as string | undefined;
    return message || "Email campaign sent.";
  },

  createShareLink: (_input, output) => {
    const url = output.url || output.share_link || output.link;
    if (url) return `Share link created: ${url}`;
    return "Share link created.";
  },

  updateFloat: (_input, output) => {
    const float = output.float as Record<string, unknown> | undefined;
    const name = float?.name || "the float";
    return `Updated **${name}**.`;
  },

  deleteFloat: () => "Float deleted.",
  deleteCandidate: () => "Candidate deleted.",
  deleteContact: () => "Contact deleted.",

  duplicateFloat: (_input, output) => {
    const float = output.float as Record<string, unknown> | undefined;
    const name = float?.name || "float";
    return `Duplicated float — new copy: **${name}**.`;
  },

  searchCandidates: (_input, output) => {
    const candidates = output.candidates as unknown[] | undefined;
    const count = candidates?.length ?? (output.total as number | undefined) ?? 0;
    if (count === 0) return "No candidates found matching your criteria.";
    return `Found **${count}** candidate${count !== 1 ? "s" : ""} matching your search.`;
  },

  searchContacts: (_input, output) => {
    const contacts = output.contacts as unknown[] | undefined;
    const count = contacts?.length ?? (output.total as number | undefined) ?? 0;
    if (count === 0) return "No contacts found matching your criteria.";
    return `Found **${count}** contact${count !== 1 ? "s" : ""}.`;
  },

  searchFloats: (_input, output) => {
    const floats = output.floats as unknown[] | undefined;
    const count = floats?.length ?? (output.total as number | undefined) ?? 0;
    if (count === 0) return "No floats found matching your criteria.";
    return `Found **${count}** float${count !== 1 ? "s" : ""}.`;
  },

  queryData: (_input, output) => {
    const data = output.data as Record<string, unknown>[] | undefined;
    const annotation = output.annotation as Record<string, unknown> | undefined;
    const count = data?.length ?? 0;

    if (count === 0) return "No results found.";

    // Single-row result (e.g. a scalar aggregate) — show the actual value(s),
    // not "Retrieved 1 result". This is the whole point of the query.
    if (count === 1) {
      const formatted = formatQueryRow(data![0], annotation);
      return formatted || "Retrieved **1** result.";
    }

    // Small result set with a name-like dimension — list the first column.
    if (count <= 5 && data) {
      const firstKey = Object.keys(data[0] ?? {}).find((k) => /name|title|label|email/i.test(k));
      if (firstKey) {
        const items = data.map((row) => String(row[firstKey])).filter(Boolean);
        if (items.length) return `${items.join(", ")} (**${count}**)`;
      }
    }

    return `Retrieved **${count}** rows.`;
  },

  searchWeb: (_input, output) => {
    const results = output.results as unknown[] | undefined;
    const count = results?.length ?? 0;
    const query = _input.query as string | undefined;
    if (count === 0) return query
      ? `No web results found for "${query}".`
      : "No web results found.";
    return query
      ? `Found **${count}** web result${count !== 1 ? "s" : ""} for "${query}".`
      : `Found **${count}** web result${count !== 1 ? "s" : ""}.`;
  },
};

/**
 * Whether a tool reported failure in its result rather than by throwing:
 * `{ success: false }` or `{ status: "error" }`.
 */
export function isFailedToolResult(output: unknown): boolean {
  if (!output || typeof output !== "object") return false;
  const out = output as Record<string, unknown>;
  return out.success === false || out.status === "error";
}

/** The error text of a failed result, when it carries one. */
export function getFailedResultText(output: unknown): string | null {
  if (!isFailedToolResult(output)) return null;
  const out = output as Record<string, unknown>;
  if (typeof out.error === "string" && out.error) return out.error;
  if (typeof out.message === "string" && out.message) return out.message;
  return null;
}

/**
 * Produce a concise, output-derived result line for a single completed tool call.
 * Returns markdown (may contain `**bold**` and `[links](url)`), or null when the
 * tool has no meaningful result to surface (or hasn't produced output yet).
 *
 * Used by the compact per-tool status line (`ToolThinkingDisplay`) so normal mode
 * shows the essential result value rather than only the input-derived phrase.
 */
export function getToolResultLine(toolCall: ToolCallInfo): string | null {
  const { toolName, input, finalOutput } = toolCall;

  // Skip utility tools that don't need summarising
  if (toolName === "suggestFollowUps" || toolName === "judgeConversation") {
    return null;
  }

  if (!finalOutput) return null;

  // A failure must never read as a result: surface its error instead.
  if (isFailedToolResult(finalOutput)) return getFailedResultText(finalOutput);

  const generator = summaryGenerators[toolName];
  if (generator) {
    try {
      return generator((input as ToolInput) ?? {}, finalOutput as ToolOutput);
    } catch {
      // Fall through to generic
    }
  }

  // Generic fallback using the tool's message field if present
  const output = finalOutput as ToolOutput;
  if (typeof output.message === "string") return output.message;
  if (output.status === "success" && typeof output.data === "object") {
    return `${toolName} completed successfully.`;
  }

  return null;
}
